import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Empty, Segmented, Spin, message } from 'antd';
import { EditOutlined, SaveOutlined } from '@ant-design/icons';
import TradingPageHeader from '../../components/TradingPageHeader';
import { usePortfolios } from '../../hooks/usePortfolios';
import { useIsMobile } from '../../hooks/useIsMobile';
import { smartBetaApi, SmartBetaStatus, SmartBetaView } from '../../services/smartBetaApi';
import { formatNumber } from '../../utils/numberFormat';
import ScheduleSection from './components/ScheduleSection';
import ScreeningSection from './components/ScreeningSection';
import SmartBetaDepositModal from './components/SmartBetaDepositModal';
import SmartBetaWithdrawModal from './components/SmartBetaWithdrawModal';
import SmartBetaEndModal from './components/SmartBetaEndModal';
import SmartBetaImportModal from './components/SmartBetaImportModal';
import { stateOf } from './components/RebalancePanel';
import NextPortfolioCard from './components/NextPortfolioCard';
import FlowStep, { StepState } from './components/FlowStep';
import FundAndRun from './components/FundAndRun';
import BotStatus from './components/BotStatus';
import {
  basketOf,
  basketProblem,
  describeSchedule,
  EMPTY_FORM,
  formFromSettings,
  formProblem,
  nextFormOf,
  scheduleOf,
  scheduleProblem,
  settingsOf,
  SmartBetaForm,
} from './smartBetaForm';
import { Body, Card, Page, Stat, Summary } from './styles';

const PICK_POLL_MS = 3_000;

/** e.g. "3 tickers + screens" */
const basketSummary = (v: SmartBetaView): string => {
  const n = v.settings.whitelist.length + (v.settings.screened?.length ?? 0);
  const screens = v.settings.criteria.length > 0 ? ' (screened)' : '';
  return `${n} ticker${n === 1 ? '' : 's'}${screens}`;
};

const errorText = (e: unknown): string => {
  const err = e as { response?: { data?: { message?: string | string[] } }; message?: string };
  const m = err.response?.data?.message ?? err.message ?? String(e);
  return Array.isArray(m) ? m.join('; ') : m;
};

/** Three steps that open one after the other: 1 build the basket → 2 rebalance schedule → 3 fund & run. */
const SmartBeta: React.FC = () => {
  const isMobile = useIsMobile();
  const {
    portfolios,
    selectedPortfolioId,
    setSelectedPortfolioId,
    selectedPortfolio,
    loading: loadingPortfolios,
  } = usePortfolios({
    accountType: 'EQUITY',
  });
  const [saved, setSaved] = useState<SmartBetaView | null>(null);
  const [form, setForm] = useState<SmartBetaForm>(EMPTY_FORM);
  const [nextForm, setNextForm] = useState<SmartBetaForm>(EMPTY_FORM);
  const [basketTab, setBasketTab] = useState<'current' | 'next'>('current');
  /** Shares just imported: the next portfolio is where the user decides what to do with them. */
  const [justImported, setJustImported] = useState(false);
  /** Nothing saved yet: the basket was checked and the schedule step may open. */
  const [basketConfirmed, setBasketConfirmed] = useState(false);
  const [nextBusy, setNextBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [depositOpen, setDepositOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [status, setStatus] = useState<SmartBetaStatus | null>(null);
  const [controlBusy, setControlBusy] = useState(false);

  const applyView = (view: SmartBetaView) => {
    setSaved(view);
    setForm(formFromSettings(view.settings));
    setNextForm(nextFormOf(view));
  };

  const load = useCallback(async (portfolioId: string) => {
    setLoading(true);
    setLoadError(null);
    try {
      const view = await smartBetaApi.get(portfolioId);
      setSaved(view);
      setForm(view ? formFromSettings(view.settings) : EMPTY_FORM);
      setNextForm(view ? nextFormOf(view) : EMPTY_FORM);
      setBasketConfirmed(false);
      setBasketTab('current');
      setJustImported(false);
    } catch (e) {
      setLoadError(errorText(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedPortfolioId) load(selectedPortfolioId);
  }, [selectedPortfolioId, load]);

  // Import is pressed at step 3; the next portfolio it leads to is at step 1.
  useEffect(() => {
    if (justImported)
      document.querySelector('[aria-label="Next portfolio"]')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [justImported]);

  // A saved basket's pick runs as a queued job: re-read the view until it lands.
  const picking = !!(saved?.screening?.current || saved?.screening?.next);
  useEffect(() => {
    if (!picking || !selectedPortfolioId) return;
    const timer = setInterval(async () => {
      try {
        const view = await smartBetaApi.get(selectedPortfolioId);
        if (view) setSaved(view);
      } catch {
        // the next tick tries again
      }
    }, PICK_POLL_MS);
    return () => clearInterval(timer);
  }, [picking, selectedPortfolioId]);

  const dataConfigId = saved?.dataConfigId;
  const refreshStatus = useCallback(async () => {
    if (!dataConfigId) return setStatus(null);
    try {
      setStatus(await smartBetaApi.status(dataConfigId));
    } catch {
      // a failed read keeps the last status; the next poll tries again
    }
  }, [dataConfigId]);
  const running = stateOf(status).running;

  useEffect(() => {
    refreshStatus();
    // Fills move the plan every few seconds while the bot runs; idle, a minute is plenty.
    const timer = setInterval(refreshStatus, running ? 5_000 : 60_000);
    return () => clearInterval(timer);
  }, [refreshStatus, running]);

  const control = async (action: 'start' | 'stop') => {
    if (!dataConfigId) return;
    setControlBusy(true);
    try {
      if (action === 'start') {
        const out = await smartBetaApi.start(dataConfigId);
        message.success(out.alreadyRunning ? 'Already rebalancing' : 'Rebalance started');
      } else {
        await smartBetaApi.stop(dataConfigId);
        message.success('Stopping — unfilled orders are being cancelled');
      }
    } catch (e) {
      message.error(errorText(e), 6);
    } finally {
      setControlBusy(false);
      refreshStatus();
      if (selectedPortfolioId && action === 'stop') load(selectedPortfolioId);
    }
  };

  const patch = (p: Partial<SmartBetaForm>) => setForm((f) => ({ ...f, ...p }));
  const savedForm = useMemo(() => (saved ? formFromSettings(saved.settings) : null), [saved]);
  const scheduleDirty = !savedForm || JSON.stringify(scheduleOf(form)) !== JSON.stringify(scheduleOf(savedForm));
  const basketDirty = !savedForm || JSON.stringify(basketOf(form)) !== JSON.stringify(basketOf(savedForm));
  const dirty = scheduleDirty || basketDirty;

  const save = async () => {
    if (!selectedPortfolioId || formProblem(form)) return;
    setSaving(true);
    try {
      applyView(await smartBetaApi.save(selectedPortfolioId, settingsOf(form)));
      message.success('Smart beta saved');
    } catch (e) {
      message.error(errorText(e), 6);
    } finally {
      setSaving(false);
    }
  };

  const patchNext = (p: Partial<SmartBetaForm>) => setNextForm((f) => ({ ...f, ...p }));
  const nextProblem = formProblem(nextForm);
  // Compared with what the next run would trade to now: the saved next basket, else the current one.
  const nextDirty = useMemo(() => {
    if (!saved) return false;
    const against = saved.next ? basketOf(nextFormOf(saved)) : basketOf(formFromSettings(saved.settings));
    return JSON.stringify(basketOf(nextForm)) !== JSON.stringify(against);
  }, [nextForm, saved]);

  const nextAction = async (action: 'save' | 'discard') => {
    if (!saved) return;
    setNextBusy(true);
    try {
      const view =
        action === 'save'
          ? await smartBetaApi.saveNext(saved.dataConfigId, basketOf(nextForm))
          : await smartBetaApi.discardNext(saved.dataConfigId);
      setSaved(view);
      setNextForm(nextFormOf(view));
      message.success(action === 'save' ? 'Next portfolio saved' : 'Next portfolio discarded');
    } catch (e) {
      message.error(errorText(e), 6);
    } finally {
      setNextBusy(false);
    }
  };

  const cancelPending = async () => {
    if (!saved) return;
    try {
      setSaved(await smartBetaApi.cancelWithdraw(saved.dataConfigId));
      message.success('Pending withdrawal cancelled');
    } catch (e) {
      message.error(errorText(e), 6);
    }
  };

  // ---------------------------------------------------------------- steps
  const scheduleStep = (state: StepState, lockedHint: string) => (
    <FlowStep
      index={2}
      title="Rebalance schedule"
      subtitle={saved && !scheduleDirty ? describeSchedule(saved.settings.schedule) : 'When the bot rebalances'}
      state={state}
      lockedHint={lockedHint}
      extra={
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          disabled={running || !!formProblem(form) || (!!saved && !dirty)}
          onClick={save}
        >
          {saved && !dirty ? 'Saved ✓' : saved ? 'Save schedule' : 'Save & continue'}
        </Button>
      }
    >
      <ScheduleSection form={form} onChange={patch} disabled={saving || running} />
      {scheduleProblem(form) && (
        <Alert type="warning" showIcon message={scheduleProblem(form)} style={{ marginTop: 8 }} />
      )}
    </FlowStep>
  );

  const fundStep = (state: StepState, lockedHint: string) => (
    <FlowStep
      index={3}
      title="Fund & run"
      subtitle="Deposit, withdraw, and the bot's rebalances"
      state={state}
      lockedHint={lockedHint}
    >
      {saved && (
        <FundAndRun
          saved={saved}
          portfolio={selectedPortfolio ?? null}
          status={status}
          running={running}
          controlBusy={controlBusy}
          blockedReason={
            dirty
              ? 'Save your changes first'
              : saved?.screening?.current
                ? 'The screens are still picking tickers'
                : null
          }
          onDeposit={() => setDepositOpen(true)}
          onWithdraw={() => setWithdrawOpen(true)}
          onImport={() => setImportOpen(true)}
          onEnd={() => setEndOpen(true)}
          onCancelPending={cancelPending}
          onStart={() => control('start')}
          onStop={() => control('stop')}
        />
      )}
    </FlowStep>
  );

  const ownBasket = !!saved;
  const basketReady = ownBasket || basketConfirmed;
  const basketStep: StepState = ownBasket ? (basketDirty ? 'active' : 'done') : basketConfirmed ? 'done' : 'active';
  const onNextTab = ownBasket && basketTab === 'next';

  const flow = (
    <>
      <FlowStep
        index={1}
        title="Build your basket"
        subtitle="Screens, white and black lists, weights"
        state={basketStep}
        extra={
          onNextTab ? null : ownBasket ? (
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              disabled={running || !basketDirty || !!formProblem(form)}
              onClick={save}
            >
              {basketDirty ? 'Save basket' : 'Saved ✓'}
            </Button>
          ) : (
            <Button
              type="primary"
              disabled={!!basketProblem(form)}
              onClick={() => setBasketConfirmed(true)}
              icon={basketConfirmed ? <EditOutlined /> : undefined}
            >
              {basketConfirmed ? 'Confirmed ✓' : 'Continue'}
            </Button>
          )
        }
      >
        {ownBasket && (
          <Segmented
            block={isMobile}
            value={basketTab}
            onChange={(v) => {
              setBasketTab(v as 'current' | 'next');
              if (v === 'current') setJustImported(false);
            }}
            options={[
              { label: 'Current portfolio', value: 'current' },
              { label: saved!.next ? 'Next portfolio •' : 'Next portfolio', value: 'next' },
            ]}
            style={{ marginBottom: 12 }}
          />
        )}
        {ownBasket && saved!.next && basketTab === 'current' && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={`Next portfolio saved — the rebalance${saved!.nextRebalance ? ` on ${saved!.nextRebalance}` : ''} trades to it`}
            action={
              <Button size="small" onClick={() => setBasketTab('next')}>
                View
              </Button>
            }
          />
        )}
        {onNextTab ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {justImported && (
              <Alert
                type="success"
                showIcon
                closable
                onClose={() => setJustImported(false)}
                message="Shares imported into the current portfolio"
                description={`Set the next portfolio to decide what the rebalance${saved!.nextRebalance ? ` on ${saved!.nextRebalance}` : ''} does with them — keep them in the white list, or leave them out to sell.`}
              />
            )}
            <NextPortfolioCard
              saved={saved!}
              form={nextForm}
              problem={nextProblem}
              dirty={nextDirty}
              locked={running}
              busy={nextBusy}
              onSave={() => nextAction('save')}
              onDiscard={() => nextAction('discard')}
            />
            <ScreeningSection
              form={nextForm}
              onChange={patchNext}
              disabled={nextBusy || running}
              screened={nextDirty ? undefined : (saved!.next ?? saved!.settings).screened}
              screening={!nextDirty && !!saved!.screening?.[saved!.next ? 'next' : 'current']}
              screenError={nextDirty ? undefined : saved!.screenError?.[saved!.next ? 'next' : 'current']}
            />
          </div>
        ) : (
          <>
            <ScreeningSection
              form={form}
              onChange={(p) => {
                patch(p);
                if (!saved) setBasketConfirmed(false);
              }}
              disabled={saving || running}
              screened={basketDirty ? undefined : saved?.settings.screened}
              screening={!basketDirty && !!saved?.screening?.current}
              screenError={basketDirty ? undefined : (saved?.screenError?.current ?? undefined)}
            />
            {basketDirty && basketProblem(form) && (form.whitelist.length > 0 || form.criteria.length > 0) && (
              <Alert type="warning" showIcon message={basketProblem(form)} style={{ marginTop: 8 }} />
            )}
          </>
        )}
      </FlowStep>
      {scheduleStep(basketReady ? (scheduleDirty ? 'active' : 'done') : 'locked', 'Build your basket first.')}
      {fundStep(ownBasket ? 'active' : 'locked', 'Save your basket and schedule first.')}
    </>
  );

  const capital = Number(saved?.totalCapital ?? 0);

  return (
    <Page>
      <TradingPageHeader
        pageName="Smart Beta"
        portfolios={portfolios}
        selectedPortfolioId={selectedPortfolioId}
        onChangePortfolio={setSelectedPortfolioId}
        loadingPortfolios={loadingPortfolios}
      />
      <Body>
        {loadError && <Alert type="error" showIcon message={loadError} />}
        {!selectedPortfolioId && !loadingPortfolios ? (
          <Empty description="Add an equity portfolio to set up a smart beta" />
        ) : (
          <Spin spinning={loading}>
            <Card style={{ marginBottom: isMobile ? 12 : 16 }}>
              <Summary>
                <Stat>
                  <BotStatus status={status} nextRebalance={saved?.nextRebalance ?? null} configured={!!saved} />
                </Stat>
                <Stat>
                  <div className="label">Basket</div>
                  <div className="value">{saved ? basketSummary(saved) : 'Not set up'}</div>
                </Stat>
                <Stat>
                  <div className="label">Rebalance</div>
                  <div className="value" title={saved ? describeSchedule(saved.settings.schedule) : undefined}>
                    {saved ? describeSchedule(saved.settings.schedule) : '—'}
                  </div>
                </Stat>
                <Stat>
                  <div className="label">Capital</div>
                  <div className="value">{formatNumber(capital)} VND</div>
                </Stat>
              </Summary>
            </Card>

            {running && (
              <Alert
                type="info"
                showIcon
                message="The bot is rebalancing: settings, deposits and withdrawals are locked until it stops."
                style={{ marginBottom: isMobile ? 12 : 16 }}
              />
            )}

            {flow}
          </Spin>
        )}
      </Body>
      {saved && (
        <SmartBetaDepositModal
          open={depositOpen}
          config={saved}
          onCancel={() => setDepositOpen(false)}
          onSuccess={(view) => {
            setDepositOpen(false);
            message.success('Deposit done');
            if (view) setSaved(view);
            else if (selectedPortfolioId) load(selectedPortfolioId);
          }}
        />
      )}
      {saved && (
        <SmartBetaEndModal
          open={endOpen}
          config={saved}
          onCancel={() => setEndOpen(false)}
          onDone={(view, selling) => {
            setEndOpen(false);
            message.success(
              selling ? 'Selling everything — the smart beta ends when the bot stops' : 'Smart beta ended',
            );
            if (view) applyView(view);
            refreshStatus();
          }}
        />
      )}
      {saved && (
        <SmartBetaImportModal
          open={importOpen}
          config={saved}
          onCancel={() => setImportOpen(false)}
          onDone={(view) => {
            setImportOpen(false);
            message.success('Shares imported');
            applyView(view);
            // Next: what the coming rebalance should do with them.
            setBasketTab('next');
            setJustImported(true);
          }}
        />
      )}
      {saved && (
        <SmartBetaWithdrawModal
          open={withdrawOpen}
          config={saved}
          onCancel={() => setWithdrawOpen(false)}
          onDone={(r) => {
            setWithdrawOpen(false);
            const paid = Number(r.paidNow);
            const pending = Number(r.pending);
            message.success(
              pending > 0
                ? `Paid ${formatNumber(paid)} VND now; ${formatNumber(pending)} VND is paid once it is sold`
                : `Withdrew ${formatNumber(paid)} VND`,
              6,
            );
            if (r.started === false && r.startError) message.warning(`Could not start selling now: ${r.startError}`, 8);
            if (r.config) setSaved(r.config);
            refreshStatus();
          }}
        />
      )}
    </Page>
  );
};

export default SmartBeta;
